import React, { useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';

import { LoaderCircular } from '@vimeo/iris/motion';

import {
  CloseButton,
  CloseIconStyled,
  FeaturedPlayer,
  NextButton,
  NextIconStyled,
  PlayerElement,
  PreviousButton,
  PreviousIconStyled,
  TitleCard,
} from '../components/PlayerLayout/PlayerLayoutStyled';
import PlayerLayoutNav from '../components/PlayerLayoutNav';
import { useSearchState } from '../components/SearchContext';
import { useShowcaseEmbed } from '../hooks/useShowcaseEmbed';
import { ClipDataObject, Pictures } from '../types';

interface Props {
  albumName: string;
  brandColor: string;
  customLogo: Pictures;
  webCustomLogo: boolean;
  description: string;
  onNext: VoidFunction;
  onPlay: VoidFunction;
  onPrevious: VoidFunction;
  onStop: VoidFunction;
  openShare: VoidFunction;
  selectedVideo: ClipDataObject;
  selectedVideoId: number;
  skipTitleCard: boolean;
  totalVideos: number;
  albumId: number;
  allowShare: boolean;
  continuousPlay?: boolean;
  baseUrl: string;
  jwt: string;
  hashedPass: string;
  albumOwnerId?: number;
  teamRole?: string;
}

const FeaturedPlayerContainer = ({
  albumName,
  brandColor,
  customLogo,
  webCustomLogo,
  description,
  onNext,
  onPlay,
  onPrevious,
  onStop,
  openShare,
  selectedVideo,
  selectedVideoId,
  skipTitleCard,
  totalVideos,
  albumId,
  allowShare,
  continuousPlay = false,
  baseUrl,
  jwt,
  hashedPass,
  albumOwnerId,
  teamRole,
}: Props) => {
  const [showTitleCard, setShowTitleCard] = useState(!skipTitleCard);
  const [isPlayerActive, setIsPlayerActive] = useState(skipTitleCard);
  const [isLoadingPlayerIframe, setIsLoadingPlayerIframe] = useState(skipTitleCard);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const { isSearching } = useSearchState();
  const { getEmbedQueryParam } = useShowcaseEmbed();
  const iFrameRef = useRef<HTMLIFrameElement>(null);

  const thumbnail = selectedVideo.pictures.sizes[selectedVideo.pictures.sizes.length - 1].link;

  let iframeUrl = useMemo(
    () =>
      `${baseUrl}/showcase/${albumId}/video/${selectedVideoId}/embed?jwt=${jwt}&autoplay=${
        shouldAutoplay || continuousPlay ? 1 : 0
      }${continuousPlay ? '&autopause=0' : ''}${
        hashedPass ? `&hash=${hashedPass}` : ''
      }${getEmbedQueryParam()}`,
    [baseUrl, albumId, selectedVideoId, shouldAutoplay, continuousPlay, hashedPass],
    // Not added jwt to dependency to avoid update the url in every 14 minutes, it will reload the iframe and video which is creating issue while watching long video.
  );

  const startVideo = () => {
    if (selectedVideo.displayType === 'CLIP_PLACEHOLDER') {
      return;
    }

    setShowTitleCard(false);
    setIsPlayerActive(true);
    setIsLoadingPlayerIframe(true);
    setShouldAutoplay(true);
  };

  const stopVideo = () => {
    setShowTitleCard(true);
    setIsPlayerActive(false);
    onStop();
  };

  const handleIframePostMessage = (event: { data: string }) => {
    if (event.data === 'PLAYER_ENDED') {
      onNext();
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleIframePostMessage);
    return () => window.removeEventListener('message', handleIframePostMessage);
  });

  useEffect(() => {
    if (isPlayerActive) {
      setIsLoadingPlayerIframe(true);
      setShouldAutoplay(false);
    }
  }, [isPlayerActive]);

  useEffect(() => {
    if (skipTitleCard) {
      startVideo();
    }
  }, [skipTitleCard]);

  const onLoad = () => {
    if (iFrameRef.current && iFrameRef.current.contentWindow) {
      iFrameRef.current.contentWindow.postMessage('ATTACH_PLAYER_EVENTS', '*');
    }
    setIsLoadingPlayerIframe(false);
  };

  return (
    <FeaturedPlayer
      isPlayerActive={isPlayerActive}
      isLoadingPlayerIframe={isLoadingPlayerIframe}
      isSearching={isSearching}
    >
      {isLoadingPlayerIframe && <CenteredLoader />}
      <PlayerElement>
        {isPlayerActive && (
          <iframe
            ref={iFrameRef}
            onLoad={onLoad}
            scrolling="no"
            allowFullScreen
            allow="autoplay"
            src={iframeUrl}
            title={selectedVideo.name}
          />
        )}
      </PlayerElement>
      {showTitleCard && <TitleCard isSearching={isSearching} thumbnail={thumbnail} />}
      {showTitleCard && (
        <PlayerLayoutNav
          albumName={albumName}
          brandColor={brandColor}
          customLogo={customLogo}
          webCustomLogo={webCustomLogo}
          description={description}
          videoTotal={totalVideos}
          onPlay={onPlay}
          openShare={openShare}
          allowShare={allowShare}
          albumOwnerId={albumOwnerId}
          teamRole={teamRole}
        />
      )}
      {isPlayerActive && (
        <div>
          <PreviousButton onClick={onPrevious}>
            <PreviousIconStyled />
          </PreviousButton>
          <NextButton onClick={onNext}>
            <NextIconStyled />
          </NextButton>
          <CloseButton onClick={stopVideo}>
            <CloseIconStyled />
          </CloseButton>
        </div>
      )}
    </FeaturedPlayer>
  );
};

export default FeaturedPlayerContainer;

const CenteredLoader = styled(LoaderCircular)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
